import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {
  BillingPaymentModal,
  EPaymentMode,
  PaymentResponses,
  PaymentStatus,
} from './payment.model';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PaymentModalService } from './payment-modal.service';
import { SalesOrder } from '@core/domain-classes/sales-order';
import { ToastrService } from 'ngx-toastr';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { CommonService } from '@core/services/common.service';
import { ICountryData, IGetAzureCountry } from '@core/domain-classes/country';
import { getAzureStreetAddress } from '@core/utils/azure-address';
import { City } from '@core/domain-classes/city';
import { StatesProvinces } from '@core/domain-classes/statesProvinces';
import { StateAndProvisionService } from '@core/services/state-and-provision.service';

@Component({
  selector: 'app-payment-modal',
  templateUrl: './payment-modal.component.html',
  styleUrls: ['./payment-modal.component.scss'],
})
export class PaymentModalComponent implements OnInit {
  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler() {
    this.closeEditModal();
  }
  @ViewChild('paymnetModal') private paymnetModal: TemplateRef<any>;
  @Input('salesOrder') salesOrder: SalesOrder;
  @Output() paymentStatus: EventEmitter<number> = new EventEmitter<number>();
  stripeSubmit: Subject<boolean> = new Subject();
  private modalRef: BsModalRef;
  public billingPaymentForm: FormGroup = <FormGroup>{};
  public yearList: number[];
  public monthList: number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  private orderId: string = '';
  public isLoading: boolean = false;
  public paymentMode: EPaymentMode;
  public isSubmitting: boolean = false;
  filteredAddresses: ICountryData[] = [];
  showDropdown = false;
  cities: City[] = [];
  statesprovinces: StatesProvinces[] = [];
  public filterCityObservable$: Subject<string> = new Subject<string>();
  public filterStateObservable$: Subject<string> = new Subject<string>();

  constructor(
    private modalService: BsModalService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private paymentService: PaymentModalService,
    private toastrService: ToastrService,
    private commonService: CommonService,
    private stateAndProvisionService: StateAndProvisionService,
    private cdr: ChangeDetectorRef
  ) {
    this.route.params.subscribe((orderId) => {
      if (
        orderId &&
        Object.keys(orderId).length > 0 &&
        orderId.hasOwnProperty('id')
      ) {
        this.orderId = orderId['id'];
      }
    });
  }

  ngOnInit(): void {
    this.createPaymentForm();
    this.getYearsList();
  }

  // This function is used to create the payment form.
  private createPaymentForm(): FormGroup {
    return (this.billingPaymentForm = this.formBuilder.group({
      orderId: new FormControl(''),
      transactionToken: new FormControl(''),
      paymentMode: new FormControl(EPaymentMode.Card),
      amount: new FormControl(),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      billingAddress: new FormControl('', [Validators.required]),
      billingAddress2: new FormControl(''),
      billingCity: new FormControl('', [Validators.required]),
      billingState: new FormControl('', [Validators.required]),
      billingZip: new FormControl('', [Validators.required]),
      cardNumber: new FormControl(''),
      currency: new FormControl('usd'),
      returnUrl: new FormControl(''),
    }));
  }

  // This function is used to open the modal.
  openModal(value: number) {
    this.paymentMode = value;
    this.modalRef = this.modalService.show(this.paymnetModal, {
      backdrop: 'static',
      class: 'modal-lg overflow-auto payment-ngbModal-window',
    });
    this.initializeBillingAddressSubscription();
  }

  // This is used tp close the modal.
  public closeEditModal(value: boolean = false): void {
    if (this.modalRef) {
      this.billingPaymentForm.reset();
      this.billingPaymentForm.updateValueAndValidity();
      this.modalRef.hide();
    }
  }

  // This function is used to create the year list to bind in the dropDown.
  public getYearsList(): void {
    const year = new Date().getFullYear();
    const range = [];
    range.push(year);
    for (var i = 1; i <= 10; i++) {
      range.push(year + i);
    }
    this.yearList = range;
  }

  public submit(): void {
    if (this.paymentMode === EPaymentMode.Card) {
      this.stripeSubmit.next(true);
    } else {
      this.processPayment(null);
    }
  }

  public sendStripe(event: any): void {
    this.processPayment(event);
  }

  public purchaseBtnProcessing(event: boolean): void {
    this.isSubmitting = event;
  }

  // This function is used to make the payment for both ONLINE and COD.
  private processPayment(paymentObject: any): void {
    this.isLoading = true;
    const currency =
      this.salesOrder.customer.priceLevel.name === 'US' ? 'usd' : 'cad';
    let paymnetDto: BillingPaymentModal = this.billingPaymentForm.value;
    paymnetDto = {
      orderId: this.orderId,
      transactionToken:
        this.paymentMode === EPaymentMode.Card ? paymentObject.id : null,
      paymentMode: this.paymentMode,
      amount:
        this.salesOrder && Object.keys(this.salesOrder).length > 0
          ? Number(Number(this.salesOrder.totalAmount).toFixed(2))
          : 0,
      firstName: paymnetDto.firstName,
      lastName: paymnetDto.lastName,
      billingAddress: paymnetDto.billingAddress,
      billingAddress2: paymnetDto.billingAddress2,
      billingCity: paymnetDto.billingCity,
      billingState: paymnetDto.billingState,
      billingZip: paymnetDto.billingZip,
      cardNumber:
        this.paymentMode === EPaymentMode.Card ? paymentObject.last4 : null,
      currency: currency,
      returnUrl:
        this.paymentMode === EPaymentMode.Card ? window.location.href : null,
    };

    this.paymentService.makeBillingPayment(paymnetDto).subscribe({
      next: (result: PaymentResponses) => {
        if (result && result.success) {
          this.toastrService.success(result.message);
          this.isLoading = false;
          this.paymentStatus.emit(PaymentStatus.Paid);
          this.billingPaymentForm.reset();
          this.billingPaymentForm.updateValueAndValidity();
          this.closeEditModal();
        } else {
          if (result && result.errors && result.errors.length > 0) {
            this.isLoading = false;
            this.toastrService.error(result.errors[0]);
            this.paymentStatus.emit(PaymentStatus.Fail);
            this.closeEditModal();
          } else if (
            result &&
            result.errorMessages &&
            result.errorMessages.length > 0
          ) {
            this.isLoading = false;
            this.toastrService.error(result.errorMessages[0]);
            this.paymentStatus.emit(PaymentStatus.Fail);
            this.closeEditModal();
          }
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastrService.error('Payment Failed');
        this.closeEditModal();
      },
    });
  }

  private getAddress(value: string): void {
    this.commonService
      .getAzureAddress(value)
      .subscribe((response: IGetAzureCountry) => {
        if (
          response &&
          response.success &&
          response.data &&
          response.data.length > 0
        ) {
          this.filteredAddresses = response.data;
          this.showDropdown = true;
        } else {
          this.filteredAddresses = [];
        }
        this.cdr.detectChanges();
      });
  }

  async onAddressSelected(address: ICountryData) {
    const stateResponse = await this.getStateByStateId(address.azureStateId);
    if (stateResponse) {
      const cityResponse = await this.getCityByStateIdAndCityName(
        address.azureStateId,
        address.azureCityName,
        address.azureCityId
      );
      if (cityResponse) {
        this.billingPaymentForm.patchValue(
          {
            billingAddress: getAzureStreetAddress(address),
            billingCity: this.cities[0].cityName,
            billingState: this.statesprovinces[0].stateName,
            billingZip: address.azurePostalCode,
          },
          { emitEvent: false }
        );
        this.showDropdown = false;
      }
      this.cdr.detectChanges();
    }
  }

  private async getStateByStateId(stateId: string): Promise<boolean> {
    try {
      const response: StatesProvinces = await this.stateAndProvisionService
        .getStatesProvinceById(stateId)
        .toPromise();
      if (response) {
        this.statesprovinces = [response];
        return true;
      }
    } catch (error) {
      console.error('Error fetching state by state ID:', error);
    }
    return false;
  }

  private async getCityByStateIdAndCityName(
    stateId: string,
    cityName: string,
    cityId: string
  ): Promise<boolean> {
    try {
      const response: City[] = await this.commonService
        .searchCitiesByStateId(stateId, cityName)
        .toPromise();
      if (response && response.length > 0) {
        const filteredCity: City[] = response.filter((c) => c.id === cityId);
        if (filteredCity && filteredCity.length > 0) {
          this.cities = filteredCity;
          return true;
        }
      }
    } catch (error) {
      console.error('Error fetching city by state ID and city name:', error);
    }
    return false;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event) {
    if (!(event.target as HTMLElement).closest('.address-dropdown')) {
      this.showDropdown = false;
    }
  }

  onAddressInputClick() {
    if (this.filteredAddresses.length > 0) {
      this.showDropdown = true;
    }
  }

  // Getter function to get the PAYMNET ENUM.
  public get EPaymentMode() {
    return EPaymentMode;
  }

  // This getter function is used to change the text for Online and COD
  public get btnText(): string {
    return this.paymentMode === EPaymentMode.Card ? 'Make Payment' : 'Proceed';
  }

  private initializeBillingAddressSubscription(): void {
    this.billingPaymentForm
      .get('billingAddress')
      .valueChanges.pipe(
        debounceTime(1000),
        distinctUntilChanged((x, y) => x.trim() === y.trim())
      )
      .subscribe((c) => {
        if (c && c.length >= 3) {
          this.getAddress(c);
        } else if (c.length == 0) {
          this.filteredAddresses = [];
          this.showDropdown = false;
        }
      });
  }
}
